import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Truck, Plus } from "lucide-react";

export default function Dispatch() {
  const [dispatches, setDispatches] = useState([]);
  const [packs, setPacks] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ pack_id: "", vehicle_no: "", driver_name: "", driver_phone: "", eway_bill: "", destination: "", notes: "" });

  const load = async () => {
    const [d, p] = await Promise.all([api.get("/warehouse/dispatches"), api.get("/warehouse/packs")]);
    setDispatches(d.data); setPacks(p.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post("/warehouse/dispatches", form);
      toast.success("Dispatched — gate-out logged");
      setOpen(false); setForm({ pack_id: "", vehicle_no: "", driver_name: "", driver_phone: "", eway_bill: "", destination: "", notes: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const readyPacks = packs.filter((p) => p.status === "PACKED");

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="warehouse-dispatch">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black flex items-center gap-2">
            <Truck className="w-7 h-7 text-primary" /> Dispatch & Gate-Out
          </h1>
          <p className="text-muted-foreground">Vehicle assignment, e-way bill capture, gate-out timestamp.</p>
        </div>
        <Button onClick={() => setOpen(true)} disabled={readyPacks.length === 0} data-testid="disp-new"><Plus className="w-4 h-4 mr-1" /> New dispatch</Button>
      </div>

      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Number</TableHead><TableHead>Pack #</TableHead><TableHead>Vehicle</TableHead>
            <TableHead>Driver</TableHead><TableHead>E-way bill</TableHead><TableHead>Destination</TableHead>
            <TableHead>Gate-out</TableHead><TableHead>Status</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {dispatches.map((d) => (
              <TableRow key={d.id} data-testid={`disp-row-${d.number}`}>
                <TableCell className="font-mono text-xs">{d.number}</TableCell>
                <TableCell className="font-mono text-xs">{d.pack_number}</TableCell>
                <TableCell className="font-mono text-xs">{d.vehicle_no}</TableCell>
                <TableCell>{d.driver_name || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{d.eway_bill || "—"}</TableCell>
                <TableCell>{d.destination || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{(d.gate_out_at || "").slice(0, 16).replace("T", " ")}</TableCell>
                <TableCell><Badge>{d.status}</Badge></TableCell>
              </TableRow>
            ))}
            {dispatches.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-10">No dispatches yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New dispatch</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Pack (PACKED only)</Label>
              <Select value={form.pack_id} onValueChange={(v) => setForm({ ...form, pack_id: v })}>
                <SelectTrigger data-testid="disp-pack"><SelectValue placeholder="Select pack…" /></SelectTrigger>
                <SelectContent>{readyPacks.map((p) => <SelectItem key={p.id} value={p.id}>{p.number} · {p.barcode}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Vehicle no.</Label><Input value={form.vehicle_no} onChange={(e) => setForm({ ...form, vehicle_no: e.target.value })} data-testid="disp-vehicle" placeholder="GJ-13 AB 1234" /></div>
              <div><Label>Driver name</Label><Input value={form.driver_name} onChange={(e) => setForm({ ...form, driver_name: e.target.value })} data-testid="disp-driver" /></div>
              <div><Label>Driver phone</Label><Input value={form.driver_phone} onChange={(e) => setForm({ ...form, driver_phone: e.target.value })} /></div>
              <div><Label>E-way bill #</Label><Input value={form.eway_bill} onChange={(e) => setForm({ ...form, eway_bill: e.target.value })} data-testid="disp-eway" /></div>
              <div className="col-span-2"><Label>Destination</Label><Input value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} data-testid="disp-dest" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={!form.pack_id || !form.vehicle_no} data-testid="disp-save">Gate out</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
